/**
 * EL ESTADO DE UNA AGENDA EN «TODAS» (administración, 14/10/2026)
 * ---------------------------------------------------------------
 * Al pulsar «Todas» en la Agenda salen, por este orden: las agendas con
 * pacientes, las de los doctores DISPONIBLES ese día aunque no tengan
 * pacientes y, al final, las bloqueadas y las de doctores de vacaciones o con
 * el día libre. Bajo el nombre de cada columna, una línea que lo dice:
 * «Disponible» en verde, o «Vacaciones», «Día libre» o «Bloqueada: motivo» en
 * rojo (la rejilla de esas va además en rojo, como siempre).
 *
 * Con el MISMO formato que el indicador del médico visitante
 * (`VisitantesColumna`, MedicoVisitanteDia.jsx): una línea pequeña de alto
 * fijo (h-4), cortada con «…» si no cabe y entera en el título. Las columnas
 * sin estado guardan el hueco (CitasPage) para que las rejillas empiecen a la
 * par. Qué estado tiene cada agenda lo dice el servidor
 * (GET /api/agendas-disponibles/dia), agenda por agenda según SU bloqueo: si
 * el doctor está de vacaciones pero a esta agenda le falta el bloqueo (se dio
 * de alta después), «Vacaciones (falta bloquear esta agenda)»: en ella aún se
 * puede citar, y se completa en Agendas disponibles.
 *
 * Con horas (27/10/2026): si la disponibilidad de ese día dice de cuánto a
 * cuánto viene, «Disponible: 09:00–14:00». Solo informa: no bloquea citas.
 */
import React from 'react';
import { CalendarCheck, CalendarOff } from 'lucide-react';

export const ESTADOS_CON_LINEA = ['disponible', 'bloqueada', 'vacaciones', 'dia_libre'];
export const ESTADOS_ROJOS = ['bloqueada', 'vacaciones', 'dia_libre'];

/** El texto de la línea: «Disponible», «Disponible: 09:00–14:00»,
 *  «Vacaciones», «Bloqueada: Festivo»… */
export function textoEstadoAgenda(estado, motivo = '', faltaBloqueo = false, horas = '') {
  if (estado === 'disponible') return horas ? `Disponible: ${horas}` : 'Disponible';
  const falta = faltaBloqueo ? ' (falta bloquear esta agenda)' : '';
  if (estado === 'vacaciones') return `Vacaciones${falta}`;
  if (estado === 'dia_libre') return `Día libre${falta}`;
  if (estado === 'bloqueada') return motivo ? `Bloqueada: ${motivo}` : 'Bloqueada';
  return '';
}

export default function EstadoAgendaColumna({ estado, motivo, faltaBloqueo = false, horas = '', testId }) {
  if (!ESTADOS_CON_LINEA.includes(estado)) return null;
  const rojo = ESTADOS_ROJOS.includes(estado);
  const texto = textoEstadoAgenda(estado, motivo, faltaBloqueo, horas);
  const titulo = faltaBloqueo
    ? `${texto}: el doctor no está, pero esta agenda aún deja citar. Complétalo en Agendas disponibles.`
    : rojo ? `${texto}: no se dan citas ese día`
      : horas ? `El doctor está disponible este día de ${horas.replace(/–/g, ' a ')}`
        : 'El doctor está disponible este día';
  const Icono = rojo ? CalendarOff : CalendarCheck;
  const [principal, ...resto] = texto.split(': ');
  return (
    <p
      className={`relative mb-1 flex h-4 min-w-0 items-center gap-1 overflow-hidden px-1 text-[11px] leading-4 ${
        rojo ? 'text-[#B91C1C]' : 'text-[#15803d]'}`}
      title={titulo}
      data-testid={testId}
      data-estado={estado}
      data-falta-bloqueo={faltaBloqueo ? 'si' : undefined}
    >
      <Icono className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate">
        <span className="font-semibold">{principal}{resto.length ? ':' : ''}</span>
        {resto.length ? ` ${resto.join(': ')}` : ''}
      </span>
    </p>
  );
}
